import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "../components/site/Footer";

export default function TermsOfService() {
  useEffect(() => {
    document.title = "Terms of Service · Elevare Solutions";
    // Basic meta description for SEO + payment-provider crawlers.
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content");
    if (desc) desc.setAttribute("content",
      "Elevare Solutions Terms of Service — payment terms, refund policy (all payments final and non-refundable), warranties, liability, and use of AI tools.");
    window.scrollTo(0, 0);
    return () => { if (desc && prev) desc.setAttribute("content", prev); };
  }, []);

  const updated = "August 4, 2026";

  return (
    <div className="min-h-screen bg-[#ded9cb] text-[#1e3d3a]">
      <header className="border-b border-black/10 bg-[#eae7de]/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="terms-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
            <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-black/40">Updated {updated}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="terms-page">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#a3823f]">Legal</p>
        <h1 className="mt-4 font-head text-4xl lg:text-5xl font-medium tracking-tight">Terms of Service</h1>
        <p className="mt-6 text-sm text-black/60 leading-relaxed">
          These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the websites,
          software, dashboards, AI tools, reports, and consulting services (collectively, the
          &ldquo;Services&rdquo;) offered by Elevare Solutions (&ldquo;Elevare,&rdquo; &ldquo;we,&rdquo;
          &ldquo;our,&rdquo; &ldquo;us&rdquo;). By booking a diagnostic, submitting payment,
          creating an account, or otherwise using the Services you agree to be bound by these Terms.
        </p>

        <Section title="1. Services">
          Elevare provides business turnaround consulting, forensic expense analysis, BBB
          reputation-repair advisory, AI-generated forecasts and recovery plans, secure client vault
          storage, and related digital deliverables. All Services are provided &ldquo;as is&rdquo;
          and &ldquo;as available.&rdquo; Elevare may modify, suspend, or discontinue any Service at
          any time without liability.
        </Section>

        <Section title="2. Payments">
          Fees for Services (including retainers, monthly subscriptions, diagnostic bookings,
          engagement fees, à-la-carte add-ons, ACH transfers, and card payments) are billed through
          Stripe or another payment processor we may designate. You authorise us and our processor
          to charge the payment method you provide for all applicable fees.
        </Section>

        <Section title="2A. Recurring ACH Debit Authorization">
          By linking a bank account and signing the Recurring ACH Debit Authorization inside your
          Customer Portal, you authorize <strong>Elevare Solutions LLC</strong> to initiate
          <strong> recurring electronic (ACH) debit entries</strong> from the account you have linked
          for all Services you contract with us, on the schedule and in the amounts set forth in
          your service agreement. This authorization remains in <strong>full force and effect until
          you revoke it in writing</strong> &mdash; either by revoking from your portal or by
          emailing kalib@elevaresolutions.me &mdash; and Elevare requires at least three (3) business
          days&rsquo; notice before the next scheduled debit to process a revocation. If a debit is
          returned by your bank for any reason (insufficient funds, closed account, invalid account,
          etc.), you agree that (a) Elevare may re-attempt the debit up to two additional times as
          permitted by NACHA rules, and (b) you remain liable for the amount owed plus any returned-item
          fees. You represent that you are an owner or authorized signer on the account. Elevare
          keeps a record of each signed authorization (name typed, date, IP address, terms version)
          for compliance with the E-Sign Act and NACHA rules and will provide you a copy upon request.
        </Section>

        <Section title="3. Refund Policy — All Payments Final">
          <strong className="text-[#1e3d3a]">All payments made to Elevare are final, non-refundable,
          and non-transferable</strong> once processed, regardless of usage, cancellation,
          non-completion of any milestone, results achieved, or subsequent change in circumstances.
          Elevare does not issue partial, pro-rated, or courtesy refunds. By submitting payment you
          expressly waive any right to chargeback, reversal, or dispute except where required by
          applicable non-waivable law. Please contact us at kalib@elevaresolutions.me with billing
          questions <em>before</em> submitting payment.
        </Section>

        <Section title="4. Cancellation">
          You may cancel a recurring subscription at any time from your Customer Portal or by
          emailing kalib@elevaresolutions.me. Cancellation stops future billing at the end of the
          current paid period. To stop recurring ACH debits specifically, revoke your Recurring ACH
          Debit Authorization from your portal or by email &mdash; Elevare requires at least three
          (3) business days&rsquo; notice before the next scheduled debit. Fees already paid remain
          non-refundable per Section 3.
        </Section>

        <Section title="5. AI Tools & Forecasts">
          Our Services include AI-generated content (forecasts, recovery plans, prospect research,
          reputation drafts, expense analyses). AI output is provided for informational purposes
          only, may contain errors, and is not legal, tax, investment, or professional advice.
          Projected savings, BBB rating improvements, revenue forecasts, and timelines are
          illustrative estimates &mdash; not guarantees. You are responsible for reviewing all AI
          output before acting on it.
        </Section>

        <Section title="6. Client Data & Confidentiality">
          You retain ownership of the business data you submit. You grant Elevare a limited licence
          to process that data solely to deliver the Services. Sensitive banking data is collected
          only through Stripe Financial Connections and Stripe Identity &mdash; Elevare does not
          store account numbers, routing numbers, full card numbers, or Social Security numbers on
          its own servers. Documents uploaded to the client vault are encrypted at rest.
        </Section>

        <Section title="7. Acceptable Use">
          You will not: (a) upload unlawful content; (b) attempt to reverse engineer or overload the
          Services; (c) use the Services to defraud a third party; (d) submit another party&rsquo;s
          data without authorisation; or (e) misrepresent your identity or the business you
          represent.
        </Section>

        <Section title="8. Warranty Disclaimer">
          THE SERVICES ARE PROVIDED &ldquo;AS IS,&rdquo; WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
          IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR
          NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED OPERATION OR ANY SPECIFIC RESULT.
        </Section>

        <Section title="9. Limitation of Liability">
          TO THE FULLEST EXTENT PERMITTED BY LAW, ELEVARE&rsquo;S AGGREGATE LIABILITY ARISING OUT OF
          OR RELATING TO THE SERVICES SHALL NOT EXCEED THE FEES ACTUALLY PAID BY YOU FOR THE
          SPECIFIC SERVICE GIVING RISE TO THE CLAIM IN THE THREE (3) MONTHS PRECEDING THE EVENT.
          ELEVARE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR
          PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR LOST DATA, EVEN IF ADVISED OF THE POSSIBILITY.
        </Section>

        <Section title="10. Indemnification">
          You agree to indemnify and hold Elevare, its founders (Kalib Whaley, John Caron), and
          affiliates harmless from any claim, loss, or expense arising from your use of the Services
          or breach of these Terms.
        </Section>

        <Section title="11. Governing Law & Disputes">
          These Terms are governed by the laws of the State in which Elevare Solutions is
          principally located, without regard to conflict-of-laws principles. Any dispute shall be
          resolved by binding arbitration or in the courts of that State, at Elevare&rsquo;s
          election. You waive any right to a jury trial or class action.
        </Section>

        <Section title="12. Changes">
          We may update these Terms at any time by posting a revised version on this page. Your
          continued use of the Services after the &ldquo;Updated&rdquo; date above constitutes
          acceptance of the revised Terms.
        </Section>

        <Section title="13. Contact">
          Elevare Solutions<br />
          Kalib Whaley &mdash; Founder &amp; CEO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:kalib@elevaresolutions.me">kalib@elevaresolutions.me</a> &mdash; (260) 200-0907<br />
          John Caron &mdash; Co-Founder &amp; COO &mdash; <a className="text-[#a3823f] hover:underline" href="mailto:john@elevaresolutions.me">john@elevaresolutions.me</a> &mdash; (931) 802-3183
        </Section>

        <p className="mt-16 text-[10px] uppercase tracking-[0.22em] text-black/40">
          Last updated {updated}. See also the <Link className="text-[#a3823f] hover:underline" to="/privacy-policy">Privacy Policy</Link>.
        </p>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12">
      <h2 className="font-head text-xl text-[#1e3d3a]">{title}</h2>
      <p className="mt-3 text-sm text-black/70 leading-relaxed">{children}</p>
    </section>
  );
}
